/**
 * The live scanner's other frame source: the Android shell's camera.
 *
 * The division of labour is the whole point (ADR 0014):
 *
 *  - **the shell is the eye.** It binds CameraX once, previews, and reports three
 *    numbers about the frame - luma, texture, motion - plus a still when asked.
 *  - **this file translates.** The thresholds that turn those numbers into "a card
 *    is framed and still" live here, in TypeScript, where a test can move them. A
 *    shell that also decided *what* a card is would be a second implementation of
 *    numbers ADR 0006 and ADR 0008 measured on the TypeScript one.
 *  - **the shared core is the judge.** `grab()` runs `readCapture` on the still, so
 *    a false tripwire costs one discarded capture and never a wrong card.
 *
 * The still is 640 wide rather than the 320 the core rectifies to, because the
 * phone's analysis frame is larger than a browser's canvas grab and throwing that
 * detail away before the detector sees it is free accuracy given up.
 */

import type { CapacitorBridge } from '../ota/host.js';
import { readCapture, type CaptureRead } from './capture.js';
import type { Observation } from './live.js';
import { decodeFrame, type LiveFrameMessage, type LiveScanCardsPlugin, type NativeCaptureMessage } from './native.js';
import type { CameraSource } from './camera-source.js';

/** The width the shell downsamples a still to. Wider than the browser's, on purpose. */
export const LIVE_CAPTURE_WIDTH = 640;

/**
 * How much texture a frame needs before the shutter considers it occupied.
 *
 * The unit is the one `findCardQuad` already uses for structure: the mean absolute
 * difference between a pixel and its neighbours, in grey levels. A flat desk is a
 * few levels of noise, a printed card is tens. Nine is the detector's own default
 * floor, reused rather than invented, and it is a *starting* value - the scanner's
 * diagnostics line exists so it can be tuned from a phone.
 */
export const LIVE_EDGE_FLOOR = 9;

/** How still the frame has to be, in grey levels of change between looks. */
export const LIVE_MOTION_CEILING = 5;

/** A frame this dark or this blown was metered wrong, not hiding a card. */
export const LIVE_LUMA_BAND = { min: 12, max: 250 } as const;

/**
 * Three numbers from the shell, as an observation.
 *
 * There is no "small" and no "skewed" here: the tripwire cannot see a quad, so it
 * cannot tell a card that is too far from one that is at an angle. It says the frame
 * is occupied; the detector says the rest, and when it refuses the still the sheet
 * asks for a better look instead of claiming the card is missing.
 */
export function observationFromLiveFrame(frame: LiveFrameMessage): Observation {
  const exposed = frame.luma >= LIVE_LUMA_BAND.min && frame.luma <= LIVE_LUMA_BAND.max;
  const occupied = exposed && frame.edge >= LIVE_EDGE_FLOOR;
  return {
    card: occupied ? 'framed' : 'none',
    steady: occupied && frame.motion <= LIVE_MOTION_CEILING,
    detail: `luma ${frame.luma.toFixed(0)} · edge ${frame.edge.toFixed(0)} · motion ${frame.motion.toFixed(1)}`,
  };
}

/**
 * The live plugin, when this shell has one.
 *
 * Capability detection rather than a version check, because the web bundle and the
 * shell update on different schedules: a bundle that calls `startLive` on a shell
 * from before this feature would otherwise throw on the first scan. An older shell
 * simply falls back to the browser camera, which still works.
 */
export function liveScanPlugin(bridge: CapacitorBridge | null): LiveScanCardsPlugin | null {
  const plugin = bridge?.plugin<Partial<LiveScanCardsPlugin>>('ScanCards');
  if (
    plugin === undefined ||
    typeof plugin.startLive !== 'function' ||
    typeof plugin.captureLive !== 'function' ||
    typeof plugin.stopLive !== 'function'
  ) {
    return null;
  }
  return plugin as LiveScanCardsPlugin;
}

/** The shell's camera, behind the same seam as the browser's. */
export function nativeCameraSource(plugin: LiveScanCardsPlugin): CameraSource {
  let latest: LiveFrameMessage | null = null;
  let listener: { remove(): Promise<void> } | null = null;
  let torch = false;

  return {
    kind: 'native',

    async start() {
      const started = await plugin.startLive({ width: LIVE_CAPTURE_WIDTH });
      torch = started.torch;
      latest = null;
      listener = await plugin.addListener('liveFrame', (frame) => {
        latest = frame;
      });
      return { torch };
    },

    async stop() {
      await listener?.remove().catch(() => undefined);
      listener = null;
      latest = null;
      await plugin.stopLive().catch(() => undefined);
    },

    async read() {
      // No look yet is not an empty frame: the sheet shows "point at a card" for the
      // first eighth of a second, which is exactly what it would show anyway.
      return latest === null ? { card: 'none', steady: false } : observationFromLiveFrame(latest);
    },

    async grab() {
      return readFromMessage(await plugin.captureLive());
    },

    async setTorch(on) {
      if (!torch) throw new Error('this camera has no light to switch on');
      await plugin.setTorch({ on });
    },

    async focusAt(x, y) {
      await plugin.focusAt({ x, y });
    },
  };
}

/**
 * A captured still, read by the shared core.
 *
 * A message whose geometry disagrees with its pixel count is a finding, not an
 * exception: the sheet says so and asks for another look, because a card shop is
 * not a place to crash the scanner into working.
 */
function readFromMessage(message: NativeCaptureMessage): CaptureRead | null {
  const decoded = decodeFrame(message);
  return decoded.frame === null ? null : readCapture(decoded.frame);
}
